import axios from "axios";
import config from "config";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";

const getUnivCookie = () => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; minted_univ_cookie_id=`);

  return parts.length === 2 ? parts.pop().split(";").shift() : "";
};

const getLaunchDarklyContext = (
  userIdentifier,
  isLoggedIn = false,
  isEmployee = false,
) => {
  return {
    isEmployee,
    isLoggedIn,
    key: userIdentifier || "anonymous-user",
    kind: "user",
  };
};

const useLaunchDarkly = Boolean(config.get("launchDarklyClientSideId"));

// Define specific flags that the application uses to optimize flag evaluation
export const FRONTEND_FLAGS = {
  guestEmailsEnabled: "guest-emails-enabled", // Boolean
  OINewMobileExperience: "oi-new-mobile-experience", // Boolean
};

export const TRACKING = {
  ClickablePreview: {
    OIpreviewSectionEditClicked: "OIpreviewSectionEditClicked",
  },
  GlobalStyling: {
    CMYKColorsSelected: "cmykColorsSelected",
    ColorPaletteCreated: "colorPaletteCreated",
    CustomColorsSelected: "customColorsSelected",
    FontEdited: "fontEdited",
    PopularColorsSelected: "popularColorsSelected",
    ReturnDashboardVisit: "returnDashboardVisit",
    ViewWebsiteClicked: "viewWebsiteClicked",
  },
};

export const LaunchDarklyProvider = ({ children, userId = null }) => {
  const [ldContext, setLdContext] = useState(null);

  // Fetch the user ID if not already set, then initialize launchdarkly
  useEffect(() => {
    if (!useLaunchDarkly) {
      return;
    }

    const cachedContext = window.sessionStorage.getItem("launchDarklyContext");

    if (cachedContext) {
      // User set from session storage, initialize client
      setLdContext(JSON.parse(cachedContext));
    } else if (userId) {
      // User ID passed in as prop, set session storage
      const newContext = getLaunchDarklyContext(`${userId}`, true);

      window.sessionStorage.setItem(
        "launchDarklyContext",
        JSON.stringify(newContext),
      );
      setLdContext(newContext);
    } else {
      // First time this session using launch darkly, set session storage
      axios
        .get(`${config.get("mintedAuthUrl")}`, { withCredentials: true })
        .then((response) => {
          // If user is not logged in set key to univ cookie instead
          let newContext;

          if (response.data.userId) {
            const userIdentifier = `${response.data.userId}`;
            const employee = response.data.email
              .toLowerCase()
              .endsWith("@minted.com");

            newContext = getLaunchDarklyContext(userIdentifier, true, employee);
            window.sessionStorage.setItem(
              "launchDarklyContext",
              JSON.stringify(newContext),
            );
          } else {
            newContext = getLaunchDarklyContext(getUnivCookie());
          }

          setLdContext(newContext);
        })
        .catch((error) => {
          console.error("Error fetching user ID:", error);
          setLdContext(getLaunchDarklyContext(null));
        });
    }
  }, [userId]);

  const WrappedComponent =
    useLaunchDarkly && ldContext
      ? withLDProvider({
          clientSideID: config.get("launchDarklyClientSideId"),
          context: ldContext,
        })(() => <>{children}</>)
      : null;

  return ldContext ? <WrappedComponent /> : children;
};
